import { useCallback, useState } from 'react';

import type { ApplyToMode } from './form.types';

interface UseProductsByApplyToModeProps {
  initialApplyTo: ApplyToMode | undefined;
  initialProductIds: number[];
}

export const useProductsByApplyToMode = ({
  initialApplyTo,
  initialProductIds,
}: UseProductsByApplyToModeProps) => {
  const [activeApplyToMode, setActiveApplyToMode] = useState<
    ApplyToMode | undefined
  >(initialApplyTo);

  const [productsByApplyTo, setProductsByApplyTo] = useState<
    Record<ApplyToMode, number[]>
  >(() => ({
    advanced: initialApplyTo === 'advanced' ? initialProductIds : [],
    products: initialApplyTo === 'products' ? initialProductIds : [],
  }));

  const [legacySelectedProducts, setLegacySelectedProducts] =
    useState<number[]>(initialProductIds);

  const selectedProductIds = initialApplyTo
    ? activeApplyToMode
      ? productsByApplyTo[activeApplyToMode]
      : []
    : legacySelectedProducts;

  const setProductsForMode = useCallback(
    (productIds: number[]) => {
      if (initialApplyTo && activeApplyToMode) {
        setProductsByApplyTo((current) => ({
          ...current,
          [activeApplyToMode]: productIds,
        }));
      } else {
        setLegacySelectedProducts(productIds);
      }
    },
    [initialApplyTo, activeApplyToMode]
  );

  const getProductsToSubmit = useCallback(() => {
    if (initialApplyTo && activeApplyToMode) {
      return productsByApplyTo[activeApplyToMode];
    }
    return legacySelectedProducts;
  }, [
    initialApplyTo,
    activeApplyToMode,
    productsByApplyTo,
    legacySelectedProducts,
  ]);

  return {
    activeApplyToMode,
    selectedProductIds,
    setActiveApplyToMode,
    setProductsForMode,
    getProductsToSubmit,
  };
};
